import {useEffect, useRef, useState} from "react";

// This hook manages the CloudAPI user lifecycle and returns an isLoading state.
// Accepts an options object for page-specific behavior:
//   onUser(cloudApiUser)  — called when user state changes
//   onLogout()            — called on explicit logout event (optional)
export function useCloudApi(options = {}) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cloudApiRef = null;

    function onLoad() {
      // window.hyWebsite may not be accessible yet on window load.
      // This happens for example in Firefox when the user navigates back after navigating away.
      if (!window.hyWebsite) {
        window.addEventListener("hyWebsiteReady", onHyWebsiteReady);
        return;
      }

      getCloudApiUser();
    }

    function onHyWebsiteReady() {
      window.removeEventListener("hyWebsiteReady", onHyWebsiteReady);
      getCloudApiUser();
    }

    // Check if a session restore is likely in progress, so we can keep
    // showing the loading state instead of flashing the login page.
    function isSessionRestorePending() {
      const websiteElement = document.querySelector(".website");
      if (websiteElement && websiteElement.getAttribute("data-hy-logged-in") === "true") {
        return true;
      }

      try {
        const cachedUser = JSON.parse(window.localStorage.getItem("hy.user"));
        if (cachedUser && cachedUser._id) return true;
      } catch (error) { /* localStorage blocked or parse error */ }

      return false;
    }

    function onUserEvent(user) {
      if (optionsRef.current.onUser) optionsRef.current.onUser(user);
      setIsLoading(false);
    }

    function getCloudApiUser() {
      const cloudApi = window.hyWebsite.getCloudAPI();
      cloudApiRef = cloudApi;

      const cloudApiUser = cloudApi.getUser();
      const { onUser, onLogout } = optionsRef.current;

      if (onLogout) cloudApi.on("logout", onLogout);
      cloudApi.on("user", onUserEvent);

      if (onUser) onUser(cloudApiUser);

      if (cloudApiUser || !isSessionRestorePending()) {
        setIsLoading(false);
      }
    }

    // On reload with cached resources, the load event may have already fired
    // before this effect runs. Call onLoad directly in that case.
    if (document.readyState === "complete") {
      onLoad();
    } else {
      window.addEventListener("load", onLoad);
    }

    return () => {
      window.removeEventListener("load", onLoad);
      window.removeEventListener("hyWebsiteReady", onHyWebsiteReady);
      if (cloudApiRef) {
        cloudApiRef.off("user", onUserEvent);
        if (optionsRef.current.onLogout) {
          cloudApiRef.off("logout", optionsRef.current.onLogout);
        }
      }
    };
  }, []);

  return [isLoading];
}
